import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useProjectStore from '../store/projectStore';
import useUIStore from '../store/uiStore';
import { getEmployees, getDepartments } from '../services/employeeService';

const initialForm = {
  name: '',
  client_name: '',
  location: '',
  department_id: '',
  project_hod_id: '',
  parent_project_id: '',
  budget_hours: '',
  budgeted_cost: '',
  budgeted_material_cost: '',
  budgeted_site_cost: '',
  weight_kg: '',
  manufacturing_start_date: '',
  manufacturing_expected_completion_date: '',
  site_installation_start_date: '',
  site_installation_expected_completion_date: '',
};

const EditProjectPage = () => {
  const { id } = useParams();
  const [form, setForm] = useState(initialForm);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasSubProjects, setHasSubProjects] = useState(false);
  const { fetchProject, updateProject, isLoading, projects, fetchProjects } = useProjectStore();
  const showToast = useUIStore((s) => s.showToast);
  const navigate = useNavigate();

  useEffect(() => {
    getEmployees().then((res) => setEmployees(res.data.employees || [])).catch(() => {});
    getDepartments().then((res) => setDepartments(res.data.departments || [])).catch(() => {});
    fetchProjects();
    fetchProject(id).then((project) => {
      if (project) {
        setForm({
          name: project.name || '',
          client_name: project.client_name || '',
          location: project.location || '',
          department_id: project.department_id || '',
          project_hod_id: project.project_hod_id || '',
          parent_project_id: project.parent_project_id || '',
          budget_hours: project.budget_hours ?? '',
          budgeted_cost: project.budgeted_cost ?? '',
          budgeted_material_cost: project.budgeted_material_cost ?? '',
          budgeted_site_cost: project.budgeted_site_cost ?? '',
          weight_kg: project.weight_kg ?? '',
          manufacturing_start_date: project.manufacturing_start_date || '',
          manufacturing_expected_completion_date: project.manufacturing_expected_completion_date || '',
          site_installation_start_date: project.site_installation_start_date || '',
          site_installation_expected_completion_date: project.site_installation_expected_completion_date || '',
        });
        setHasSubProjects((project.sub_project_count || 0) > 0);
      }
      setLoading(false);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Only top-level projects (other than this one) can be picked as a parent -
  // a project that already groups sub-projects of its own can't also become
  // one, and this project can't be its own parent.
  const eligibleParents = projects.filter((p) => !p.parent_project_id && String(p.id) !== String(id));

  const handleChange = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.manufacturing_expected_completion_date || !form.site_installation_expected_completion_date) {
      showToast('Please fill in all required fields', 'error');
      return;
    }

    const payload = {
      name: form.name,
      client_name: form.client_name || null,
      location: form.location || null,
      department_id: form.department_id ? Number(form.department_id) : null,
      project_hod_id: form.project_hod_id ? Number(form.project_hod_id) : null,
      parent_project_id: form.parent_project_id ? Number(form.parent_project_id) : null,
      budget_hours: form.budget_hours ? Number(form.budget_hours) : null,
      budgeted_cost: form.budgeted_cost ? Number(form.budgeted_cost) : null,
      budgeted_material_cost: form.budgeted_material_cost ? Number(form.budgeted_material_cost) : null,
      budgeted_site_cost: form.budgeted_site_cost ? Number(form.budgeted_site_cost) : null,
      weight_kg: form.weight_kg ? Number(form.weight_kg) : null,
      manufacturing_start_date: form.manufacturing_start_date || null,
      manufacturing_expected_completion_date: form.manufacturing_expected_completion_date,
      site_installation_start_date: form.site_installation_start_date || null,
      site_installation_expected_completion_date: form.site_installation_expected_completion_date,
    };

    const result = await updateProject(id, payload);
    if (result.success) {
      showToast('Project updated', 'success');
      navigate(`/projects/${id}`);
    } else {
      showToast(result.error, 'error');
    }
  };

  if (loading) {
    return <div className="min-h-screen p-6"><p className="text-gray-500">Loading...</p></div>;
  }

  return (
    <div className="min-h-screen p-6">
      <div className="max-w-2xl mx-auto glass-card p-6">
        <h1 className="text-xl font-bold text-gray-900 mb-6">Edit Project</h1>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Project Name *</label>
            <input
              required
              value={form.name}
              onChange={handleChange('name')}
              className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Client Name</label>
              <input
                value={form.client_name}
                onChange={handleChange('client_name')}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
              <input
                value={form.location}
                onChange={handleChange('location')}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Part of a larger project (optional)</label>
            {hasSubProjects ? (
              <p className="text-xs text-amber-600 border border-amber-200 bg-amber-50 rounded px-3 py-2">
                This project already groups its own sub-projects, so it can't also be made a sub-project of another one.
              </p>
            ) : (
              <>
                <select
                  value={form.parent_project_id}
                  onChange={handleChange('parent_project_id')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                >
                  <option value="">Not part of another project</option>
                  {eligibleParents.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <p className="text-xs text-gray-400 mt-1">
                  E.g. this is one of several tanks under a bigger client engagement - pick that project here
                  to include this one in its combined costing.
                </p>
              </>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
              <select
                value={form.department_id}
                onChange={handleChange('department_id')}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              >
                <option value="">Unassigned</option>
                {departments.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Project HOD</label>
              <select
                value={form.project_hod_id}
                onChange={handleChange('project_hod_id')}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              >
                <option value="">Unassigned</option>
                {employees.map((e) => <option key={e.id} value={e.id}>{e.name} — {e.role}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Budget Hours</label>
              <input
                type="number"
                value={form.budget_hours}
                onChange={handleChange('budget_hours')}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Budgeted Cost (₹)</label>
              <input
                type="number"
                value={form.budgeted_cost}
                onChange={handleChange('budgeted_cost')}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Weight (kg)</label>
              <input
                type="number"
                value={form.weight_kg}
                onChange={handleChange('weight_kg')}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Budgeted Material Cost (₹)</label>
              <input
                type="number"
                value={form.budgeted_material_cost}
                onChange={handleChange('budgeted_material_cost')}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                placeholder="For the Cash Flow module"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Budgeted Site Install Cost (₹)</label>
              <input
                type="number"
                value={form.budgeted_site_cost}
                onChange={handleChange('budgeted_site_cost')}
                className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                placeholder="For the Cash Flow module"
              />
            </div>
          </div>

          <div className="border-t border-gray-100 pt-4">
            <div className="text-sm font-semibold text-gray-700 mb-2">Manufacturing Phase</div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={form.manufacturing_start_date}
                  onChange={handleChange('manufacturing_start_date')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expected Completion *</label>
                <input
                  required
                  type="date"
                  value={form.manufacturing_expected_completion_date}
                  onChange={handleChange('manufacturing_expected_completion_date')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
            </div>
          </div>

          <div className="border-t border-gray-100 pt-4">
            <div className="text-sm font-semibold text-gray-700 mb-2">Site Installation Phase</div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                <input
                  type="date"
                  value={form.site_installation_start_date}
                  onChange={handleChange('site_installation_start_date')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Expected Completion *</label>
                <input
                  required
                  type="date"
                  value={form.site_installation_expected_completion_date}
                  onChange={handleChange('site_installation_expected_completion_date')}
                  className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={() => navigate(`/projects/${id}`)}
              className="px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="px-4 py-2 text-sm rounded bg-komal-blue hover:bg-komal-blue-dark text-white font-semibold disabled:opacity-60"
            >
              {isLoading ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditProjectPage;
